import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["range", "intensity"];

  static values = {
    baseSize: { type: Number, default: 128 }, // 32 * 4 = 128px (h-32/w-32)
    minIntensity: { type: Number, default: 1 },
    maxIntensity: { type: Number, default: 4 },
    defaultIntensity: { type: Number, default: 2 },
  };

  connect() {
    // Initialize the controller
    this.isResizing = false;
    this.currentElement = null;

    // Initialize intensity labels
    try {
      this.intensityLabels = JSON.parse(
        this.element.dataset.intensityLabels || "{}"
      );
    } catch (e) {
      console.error("Error parsing intensity labels:", e);
      this.intensityLabels = {};
    }

    // Bind the resize handlers to maintain context
    this.handleResize = this.handleResize.bind(this);
    this.stopResize = this.stopResize.bind(this);

    // Add global event listeners
    window.addEventListener("mousemove", this.handleResize);
    window.addEventListener("mouseup", this.stopResize);
    window.addEventListener("touchmove", this.handleResize);
    window.addEventListener("touchend", this.stopResize);

    this.changeVisualSize();
  }

  disconnect() {
    // Clean up event listeners
    window.removeEventListener("mousemove", this.handleResize);
    window.removeEventListener("mouseup", this.stopResize);
    window.removeEventListener("touchmove", this.handleResize);
    window.removeEventListener("touchend", this.stopResize);
  }

  startResize(event) {
    event.preventDefault();
    this.isResizing = true;
    this.startY = event.clientY || event.touches[0].clientY;
    this.startIntensity = this.rangeTarget.value;
    this.lastIntensity = this.startIntensity;
  }

  handleResize(event) {
    if (!this.isResizing) return;

    const clientY =
      event.clientY || (event.touches && event.touches[0].clientY);
    if (!clientY) return;
    const deltaY = this.startY - clientY;
    // To adjust sensitivity, change the step size
    const stepSize = 30;
    const intensityChange = Math.round(deltaY / stepSize);
    let newIntensity =
      parseInt(this.startIntensity) + parseInt(intensityChange);

    // Clamp intensity to exact values between 1 and 4
    newIntensity = Math.max(
      this.minIntensityValue,
      Math.min(this.maxIntensityValue, newIntensity)
    );

    // Only update if the intensity has actually changed
    if (newIntensity !== this.lastIntensity) {
      this.updateIntensity(newIntensity);
      this.lastIntensity = newIntensity;
    }
  }

  stopResize() {
    this.isResizing = false;
    this.lastIntensity = null;
  }

  updateIntensity(intensity) {
    this.rangeTarget.value = intensity;
    this.element
      .querySelector('[role="slider"]')
      ?.setAttribute("aria-valuenow", String(intensity));

    this.changeVisualSize();

    // Update the form data
    // this.updateFormData();
  }

  changeVisualSize() {
    const intensity = parseInt(this.rangeTarget.value);
    // Calculate scale based on intensity level (1-4)
    // intensity 1: 0.75x scale
    // intensity 2: 0.917x scale (default)
    // intensity 3: 1.084x scale
    // intensity 4: 1.25x scale
    const scaleMultiplier = 1 + (intensity - 1) * 0.167;

    // Apply the scale using CSS variable
    this.element.style.setProperty("--emotion-scale", scaleMultiplier);

    // Update the emotion text with the new intensity label

    const intensityLabel = this.getIntensityLabel(intensity);

    this.intensityTarget.textContent = `${intensityLabel}`;
  }

  getIntensityLabel(intensity) {
    if (!this.intensityLabels) {
      console.warn("Intensity labels not initialized");
      return "";
    }

    const intensityKey = String(intensity);
    return this.intensityLabels[intensityKey] || "";
  }

  handleResizeKeydown(event) {
    if (event.key === "ArrowUp" || event.key === "ArrowDown") {
      event.preventDefault();
      const currentIntensity = Number(this.rangeTarget.value);
      const newIntensity =
        event.key === "ArrowUp"
          ? Math.min(currentIntensity + 1, this.maxIntensityValue)
          : Math.max(currentIntensity - 1, this.minIntensityValue);

      if (newIntensity !== currentIntensity) {
        this.updateIntensity(newIntensity);
      }
    }
  }
}
